import { useQuery } from '@tanstack/react-query';
import type {
  LeaderboardEntry,
  LeaderboardPeriod,
  LeaderboardSnapshot,
  UserLeaderboardStats,
} from '@ponzinomics/types';
import { featureApi, MOCK_MODE } from '../lib';
import { useCurrentProject } from './use-current-project';

const MOCK_ENTRIES: LeaderboardEntry[] = [
  {
    rank: 1,
    userId: 'u1',
    displayName: 'CryptoWhale',
    points: 12500,
    tier: 'Diamond',
    questsCompleted: 45,
    memesCreated: 120,
  },
  {
    rank: 2,
    userId: 'u2',
    displayName: 'DiamondHands',
    points: 11200,
    tier: 'Diamond',
    questsCompleted: 38,
    memesCreated: 95,
  },
  {
    rank: 3,
    userId: 'u3',
    displayName: 'MoonBoy',
    points: 9800,
    tier: 'Gold',
    questsCompleted: 32,
    memesCreated: 78,
  },
  {
    rank: 4,
    userId: 'u4',
    displayName: 'DegenKing',
    points: 8500,
    tier: 'Gold',
    questsCompleted: 28,
    memesCreated: 65,
  },
  {
    rank: 5,
    userId: 'u5',
    displayName: 'TokenMaster',
    points: 7200,
    tier: 'Silver',
    questsCompleted: 22,
    memesCreated: 50,
  },
  {
    rank: 6,
    userId: 'u6',
    displayName: 'HODLer',
    points: 6100,
    tier: 'Silver',
    questsCompleted: 18,
    memesCreated: 42,
  },
  {
    rank: 7,
    userId: 'u7',
    displayName: 'APEish',
    points: 5400,
    tier: 'Bronze',
    questsCompleted: 15,
    memesCreated: 30,
  },
];

const MOCK_SNAPSHOT: LeaderboardSnapshot = {
  projectId: 'proj-1',
  period: 'all-time',
  entries: MOCK_ENTRIES,
  totalParticipants: 1234,
  updatedAt: new Date().toISOString(),
};

export const leaderboardKeys = {
  all: ['leaderboard'] as const,
  lists: () => [...leaderboardKeys.all, 'list'] as const,
  list: (projectId: string, period: LeaderboardPeriod) =>
    [...leaderboardKeys.lists(), projectId, period] as const,
  userRank: (projectId: string, userId: string) =>
    [...leaderboardKeys.all, 'rank', projectId, userId] as const,
};

export function useLeaderboard(
  period: LeaderboardPeriod = 'all-time'
): ReturnType<typeof useQuery<LeaderboardSnapshot>> {
  const { projectId } = useCurrentProject();

  return useQuery({
    queryKey: leaderboardKeys.list(projectId ?? '', period),
    queryFn: async () => {
      if (MOCK_MODE) return { ...MOCK_SNAPSHOT, period };
      return featureApi.get<LeaderboardSnapshot>(
        `/points/leaderboard?projectId=${projectId}&period=${period}`
      );
    },
    enabled: !!projectId,
    staleTime: 1000 * 30,
    refetchInterval: 1000 * 30,
  });
}

export function useUserRank(
  userId: string | undefined
): ReturnType<typeof useQuery<UserLeaderboardStats>> {
  const { projectId } = useCurrentProject();

  return useQuery({
    queryKey: leaderboardKeys.userRank(projectId ?? '', userId ?? ''),
    queryFn: async () => {
      if (MOCK_MODE) {
        return {
          userId: userId ?? '',
          projectId: projectId ?? '',
          rank: 42,
          points: 3200,
          percentile: 85,
        } satisfies UserLeaderboardStats;
      }
      return featureApi.get<UserLeaderboardStats>(
        `/points/leaderboard/rank/${userId}?projectId=${projectId}`
      );
    },
    enabled: !!projectId && !!userId,
    staleTime: 1000 * 60,
  });
}
